import { useEffect, useMemo, useState, useCallback } from "react";
import { Upload, X, Sparkles, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { SERVICES, SIZES, VEHICLES, formatPrice, SHOW_DEPOSIT_CTA } from "@/lib/constants";
import { fetchEstimate, submitQuote, createDepositCheckout } from "@/lib/api";

const STEPS = ["Service", "Vehicle", "Damage", "Contact"];

const initialState = {
    services: [],
    vehicle_type: "sedan",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    damage_size: "medium",
    description: "",
    photo_base64: null,
    photo_mime: null,
    photo_preview: null,
    full_name: "",
    email: "",
    phone: "",
    suburb: "",
    region: "",
};

const NZ_REGIONS = [
    "Northland",
    "Auckland",
    "Waikato",
    "Bay of Plenty",
    "Gisborne",
    "Hawke's Bay",
    "Taranaki",
    "Manawatū-Whanganui",
    "Wellington",
    "Tasman",
    "Nelson",
    "Marlborough",
    "West Coast",
    "Canterbury",
    "Otago",
    "Southland",
];

export default function QuoteEngine() {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(initialState);
    const [estimate, setEstimate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const update = useCallback(
        (patch) => setForm((f) => ({ ...f, ...patch })),
        [],
    );

    const toggleService = (key) => {
        setForm((f) => {
            const has = f.services.includes(key);
            return {
                ...f,
                services: has
                    ? f.services.filter((s) => s !== key)
                    : [...f.services, key],
            };
        });
    };

    // Apply prefill from PhotoEstimator if present
    useEffect(() => {
        const apply = () => {
            try {
                const raw = sessionStorage.getItem("pp_prefill");
                if (!raw) return;
                const p = JSON.parse(raw);
                setForm((f) => ({
                    ...f,
                    services: p.services?.length ? p.services : f.services,
                    damage_size: p.damage_size || f.damage_size,
                    vehicle_type: p.vehicle_type || f.vehicle_type,
                    description: p.description || f.description,
                    photo_base64: p.photo_base64 || f.photo_base64,
                    photo_mime: p.photo_mime || f.photo_mime,
                    photo_preview: p.photo_preview || f.photo_preview,
                }));
                // Jump to vehicle step since services & damage are detected
                setStep(1);
                sessionStorage.removeItem("pp_prefill");
                toast.success("We've pre-filled your details from the photo.");
            } catch (e) {
                /* ignore */
            }
        };
        apply();
        window.addEventListener("pp:prefill", apply);
        return () => window.removeEventListener("pp:prefill", apply);
    }, []);

    // Live rule-based estimate
    useEffect(() => {
        let active = true;
        if (!form.services.length) {
            setEstimate(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const data = await fetchEstimate({
                    services: form.services,
                    damage_size: form.damage_size,
                    vehicle_type: form.vehicle_type,
                });
                if (active) setEstimate(data);
            } catch (e) {
                /* silent */
            }
        }, 250);
        return () => {
            active = false;
            clearTimeout(t);
        };
    }, [form.services, form.damage_size, form.vehicle_type]);

    const onPhoto = (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Please upload a JPG, PNG, or WEBP image.");
            return;
        }
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image must be under 6 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result);
            const b64 = dataUrl.split(",")[1];
            update({
                photo_base64: b64,
                photo_mime: file.type,
                photo_preview: dataUrl,
            });
        };
        reader.readAsDataURL(file);
    };

    const canNext = useMemo(() => {
        if (step === 0) return form.services.length > 0;
        if (step === 1)
            return (
                form.vehicle_type &&
                form.vehicle_make.trim() &&
                form.vehicle_model.trim()
            );
        if (step === 2) return !!form.damage_size;
        if (step === 3)
            return (
                form.full_name.trim() &&
                /\S+@\S+\.\S+/.test(form.email) &&
                form.phone.trim().length >= 6 &&
                form.suburb.trim() &&
                form.region
            );
        return false;
    }, [step, form]);

    const handleSubmit = async () => {
        if (!canNext) return;
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                suburb: form.suburb.trim(),
                region: form.region,
                vehicle_type: form.vehicle_type,
                vehicle_make: form.vehicle_make.trim(),
                vehicle_model: form.vehicle_model.trim(),
                vehicle_year: form.vehicle_year
                    ? Number(form.vehicle_year)
                    : null,
                services: form.services,
                damage_size: form.damage_size,
                description: form.description,
                photo_base64: form.photo_base64,
                photo_mime: form.photo_mime,
            };
            const res = await submitQuote(payload);
            setResult(res);
            toast.success("Quote ready — see your smart estimate below.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not submit quote. Try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setForm(initialState);
        setEstimate(null);
        setResult(null);
        setStep(0);
    };

    return (
        <section
            id="quote"
            data-testid="quote-section"
            className="relative py-20 md:py-28 bg-carbon overflow-hidden"
        >
            <div className="absolute inset-0 ai-grid pointer-events-none" aria-hidden />
            <div className="absolute -top-20 right-0 w-[30rem] h-[30rem] bg-safety/10 rounded-full blur-[130px] pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-6 md:px-10">
                <div className="max-w-2xl mb-10">
                    <p className="uppercase text-xs tracking-[0.3em] text-electric mb-3">
                        Full Quote Form
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
                        <span className="gradient-text">
                            Prefer the detailed route?
                        </span>{" "}
                        <span className="text-safety">We&rsquo;ve got you.</span>
                    </h2>
                    <p className="text-slate-400 mt-4 leading-relaxed">
                        Tell us a bit more about your car and the damage and
                        we&rsquo;ll give you a precise AI estimate and match you
                        with a local repairer.
                    </p>
                </div>

                {!result ? (
                    <div
                        data-testid="quote-engine-card"
                        className="grid grid-cols-1 lg:grid-cols-12 glass rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
                    >
                        {/* Form panel */}
                        <div className="lg:col-span-8 p-7 md:p-10">
                            {/* Stepper */}
                            <div className="flex items-center gap-2 md:gap-3 mb-8 flex-wrap">
                                {STEPS.map((label, i) => (
                                    <div
                                        key={label}
                                        className="flex items-center gap-2 md:gap-3"
                                    >
                                        <div
                                            data-testid={`stepper-${i}`}
                                            className={`flex items-center gap-2 ${
                                                i === step
                                                    ? "text-electric"
                                                    : i < step
                                                      ? "text-slate-300"
                                                      : "text-slate-500"
                                            }`}
                                        >
                                            <span
                                                className={`w-7 h-7 grid place-items-center text-xs border rounded-xl transition-colors ${
                                                    i === step
                                                        ? "border-electric bg-electric/10 text-electric"
                                                        : i < step
                                                          ? "border-electric/40 bg-electric/5 text-electric"
                                                          : "border-white/15 text-slate-500"
                                                }`}
                                            >
                                                {i + 1}
                                            </span>
                                            <span className="text-xs uppercase tracking-[0.18em] hidden md:inline">
                                                {label}
                                            </span>
                                        </div>
                                        {i < STEPS.length - 1 && (
                                            <span className="w-5 md:w-8 h-px bg-white/15" />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Step 0: Services */}
                            {step === 0 && (
                                <div data-testid="step-services">
                                    <h3 className="font-display text-2xl text-white mb-2">
                                        What needs attention?
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-6">
                                        Pick one or more. Not sure? Just choose
                                        the closest match.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {SERVICES.map((s) => {
                                            const active =
                                                form.services.includes(s.key);
                                            return (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    data-testid={`select-service-${s.key}`}
                                                    onClick={() =>
                                                        toggleService(s.key)
                                                    }
                                                    className={`text-left p-4 border-2 transition-all duration-200 rounded-2xl group ${
                                                        active
                                                            ? "border-electric bg-electric/10 shadow-glow-cyan ring-1 ring-electric/30"
                                                            : "border-white/10 bg-carbon2 hover:border-electric/50 hover:-translate-y-1"
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div>
                                                            <div className="font-display text-lg text-white">
                                                                {s.label}
                                                            </div>
                                                            <div className="text-slate-400 text-xs mt-1">
                                                                {s.blurb}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`w-5 h-5 mt-1 border rounded-lg ${
                                                                active
                                                                    ? "bg-electric border-electric"
                                                                    : "border-white/20"
                                                            } grid place-items-center`}
                                                        >
                                                            {active && (
                                                                <CheckCircle2
                                                                    size={12}
                                                                    className="text-carbon"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Step 1: Vehicle */}
                            {step === 1 && (
                                <div data-testid="step-vehicle">
                                    <h3 className="font-display text-2xl text-white mb-2">
                                        About your car
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-6">
                                        Helps us match the right paint and
                                        parts.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <Label>Vehicle type</Label>
                                            <Select
                                                value={form.vehicle_type}
                                                onValueChange={(v) =>
                                                    update({ vehicle_type: v })
                                                }
                                            >
                                                <SelectTrigger
                                                    data-testid="vehicle-type-select"
                                                    className="h-12 rounded-xl bg-carbon2 border-white/15 text-white focus:border-electric"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {VEHICLES.map((v) => (
                                                        <SelectItem
                                                            key={v.key}
                                                            value={v.key}
                                                            data-testid={`vehicle-option-${v.key}`}
                                                        >
                                                            {v.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Field
                                            label="Make"
                                            placeholder="e.g. Toyota"
                                            value={form.vehicle_make}
                                            onChange={(v) =>
                                                update({ vehicle_make: v })
                                            }
                                            testid="vehicle-make"
                                        />
                                        <Field
                                            label="Model"
                                            placeholder="e.g. Hilux"
                                            value={form.vehicle_model}
                                            onChange={(v) =>
                                                update({ vehicle_model: v })
                                            }
                                            testid="vehicle-model"
                                        />
                                        <Field
                                            label="Year (optional)"
                                            placeholder="2018"
                                            value={form.vehicle_year}
                                            onChange={(v) =>
                                                update({
                                                    vehicle_year: v.replace(
                                                        /[^0-9]/g,
                                                        "",
                                                    ),
                                                })
                                            }
                                            testid="vehicle-year"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Damage */}
                            {step === 2 && (
                                <div data-testid="step-damage">
                                    <h3 className="font-display text-2xl text-white mb-2">
                                        How bad is it?
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-6">
                                        Pick the closest match. Add a photo for
                                        a sharper price.
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                        {SIZES.map((s) => {
                                            const active =
                                                form.damage_size === s.key;
                                            return (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    data-testid={`select-size-${s.key}`}
                                                    onClick={() =>
                                                        update({
                                                            damage_size: s.key,
                                                        })
                                                    }
                                                    className={`p-4 text-left border transition-all rounded-xl ${
                                                        active
                                                            ? "border-electric bg-electric/10 shadow-glow-cyan"
                                                            : "border-white/10 bg-carbon2 hover:border-electric/50"
                                                    }`}
                                                >
                                                    <div className="font-display text-lg text-white">
                                                        {s.label}
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-1">
                                                        {s.note}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <Label>Photo (optional)</Label>
                                    {form.photo_preview ? (
                                        <div className="relative border border-white/10 rounded-xl overflow-hidden">
                                            <img
                                                src={form.photo_preview}
                                                alt="Damage preview"
                                                data-testid="photo-preview"
                                                className="w-full max-h-72 object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    update({
                                                        photo_base64: null,
                                                        photo_mime: null,
                                                        photo_preview: null,
                                                    })
                                                }
                                                data-testid="remove-photo"
                                                className="absolute top-2 right-2 glass hover:bg-safety hover:text-white text-slate-200 p-2 transition-colors rounded-xl"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label
                                            data-testid="photo-upload-zone"
                                            className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-white/15 hover:border-electric bg-carbon2/60 hover:bg-carbon2 cursor-pointer transition-colors rounded-xl"
                                        >
                                            <Upload
                                                size={26}
                                                strokeWidth={1.4}
                                                className="text-electric"
                                            />
                                            <span className="text-slate-300 text-sm">
                                                Drop a JPG/PNG/WEBP or click to
                                                upload
                                            </span>
                                            <span className="text-slate-500 text-xs">
                                                Max 6 MB
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                className="hidden"
                                                data-testid="photo-input"
                                                onChange={(e) =>
                                                    onPhoto(e.target.files?.[0])
                                                }
                                            />
                                        </label>
                                    )}

                                    <div className="mt-5">
                                        <Label>Anything else? (optional)</Label>
                                        <textarea
                                            data-testid="description-input"
                                            value={form.description}
                                            onChange={(e) =>
                                                update({
                                                    description: e.target.value,
                                                })
                                            }
                                            rows={3}
                                            placeholder="e.g. minor scrape on rear door, no rust"
                                            className="w-full bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white placeholder:text-slate-500 px-4 py-3 text-sm rounded-xl"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Contact */}
                            {step === 3 && (
                                <div data-testid="step-contact">
                                    <h3 className="font-display text-2xl text-white mb-2">
                                        Where are you &amp; how do we reach you?
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-6">
                                        We&rsquo;ll match you with a trusted
                                        local repairer in your area who confirms
                                        your exact price. We never share your
                                        details.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <Field
                                                label="Full name"
                                                placeholder="Jane Smith"
                                                value={form.full_name}
                                                onChange={(v) =>
                                                    update({ full_name: v })
                                                }
                                                testid="contact-name"
                                            />
                                        </div>
                                        <Field
                                            label="Email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={form.email}
                                            onChange={(v) =>
                                                update({ email: v })
                                            }
                                            testid="contact-email"
                                        />
                                        <Field
                                            label="Phone"
                                            placeholder="+64 21 555 0199"
                                            value={form.phone}
                                            onChange={(v) =>
                                                update({ phone: v })
                                            }
                                            testid="contact-phone"
                                        />
                                        <Field
                                            label="Suburb / town"
                                            placeholder="e.g. Henderson"
                                            value={form.suburb}
                                            onChange={(v) =>
                                                update({ suburb: v })
                                            }
                                            testid="contact-suburb"
                                        />
                                        <div>
                                            <label className="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-2">
                                                Region
                                            </label>
                                            <select
                                                data-testid="contact-region"
                                                value={form.region}
                                                onChange={(e) =>
                                                    update({
                                                        region: e.target.value,
                                                    })
                                                }
                                                className="w-full h-12 px-3 bg-carbon2 border border-white/15 rounded-xl text-white focus:border-electric focus:ring-2 focus:ring-electric/20 outline-none transition-all"
                                            >
                                                <option value="">
                                                    Select your region…
                                                </option>
                                                {NZ_REGIONS.map((r) => (
                                                    <option key={r} value={r}>
                                                        {r}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Nav buttons */}
                            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setStep((s) => Math.max(0, s - 1))
                                    }
                                    disabled={step === 0}
                                    data-testid="step-back"
                                    className="text-sm uppercase tracking-[0.18em] text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    ← Back
                                </button>
                                {step < STEPS.length - 1 ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            canNext && setStep((s) => s + 1)
                                        }
                                        disabled={!canNext}
                                        data-testid="step-next"
                                        className="px-6 py-3 text-sm uppercase tracking-[0.15em] font-bold text-white bg-safety hover:bg-safety-dark disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 shadow-glow-orange rounded-xl"
                                    >
                                        Continue →
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={!canNext || submitting}
                                        data-testid="submit-quote"
                                        className="px-6 py-3 text-sm uppercase tracking-[0.15em] font-bold text-white bg-safety hover:bg-safety-dark disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 shadow-glow-orange inline-flex items-center gap-2 rounded-xl"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles size={16} />
                                                Get my quote
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Live estimate panel */}
                        <aside className="lg:col-span-4 bg-carbon2/60 border-t-2 lg:border-t-0 lg:border-l-2 border-safety p-7 md:p-8 relative">
                            <div className="text-xs uppercase tracking-[0.25em] text-electric mb-4 font-semibold flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-electric pulse-dot" />
                                Live Estimate
                            </div>
                            {estimate ? (
                                <>
                                    <div className="font-display font-bold text-3xl md:text-4xl text-white leading-none">
                                        {formatPrice(estimate.low)}
                                        <span className="text-slate-500 mx-2">
                                            –
                                        </span>
                                        {formatPrice(estimate.high)}
                                    </div>
                                    <div className="mt-5 space-y-2.5">
                                        {estimate.breakdown.map((b) => (
                                            <div
                                                key={b.service}
                                                className="flex items-center justify-between text-sm border-b border-white/10 pb-2"
                                            >
                                                <span className="text-slate-400">
                                                    {b.service}
                                                </span>
                                                <span className="text-white">
                                                    {formatPrice(b.estimate)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                                        Indicative range (excl. GST) —
                                        confirmed once we see the car.
                                    </p>
                                </>
                            ) : (
                                <div className="text-slate-400 text-sm">
                                    Pick a service to see an instant price
                                    range here.
                                </div>
                            )}
                        </aside>
                    </div>
                ) : (
                    <ResultPanel result={result} onReset={reset} />
                )}
            </div>
        </section>
    );
}

function Label({ children }) {
    return (
        <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5">
            {children}
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
    return (
        <div>
            <Label>{label}</Label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full h-12 bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white placeholder:text-slate-500 px-4 text-sm rounded-xl transition-colors"
            />
        </div>
    );
}

function ResultPanel({ result, onReset }) {
    const [locking, setLocking] = useState(false);

    const lockSlot = async () => {
        setLocking(true);
        try {
            const { checkout_url } = await createDepositCheckout({
                quote_id: result.id,
                origin_url: window.location.origin,
            });
            window.location.href = checkout_url;
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Couldn't start the deposit. Please try again.",
            );
            setLocking(false);
        }
    };

    return (
        <div
            data-testid="quote-result"
            className="glass p-7 md:p-10 relative overflow-hidden rounded-3xl shadow-glow-cyan"
        >
            <div className="absolute -top-32 -right-32 w-64 h-64 bg-safety/20 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute -bottom-32 -left-20 w-56 h-56 bg-electric/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                    <Sparkles size={20} className="text-electric" />
                    <span className="uppercase text-xs tracking-[0.3em] text-electric">
                        Your Smart Estimate
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <div className="text-slate-400 text-xs uppercase tracking-[0.18em] mb-2">
                            AI-refined range
                        </div>
                        <div
                            data-testid="ai-estimate-range"
                            className="font-display font-bold text-4xl md:text-5xl text-white leading-none"
                        >
                            {formatPrice(result.ai_estimate_low)}
                            <span className="text-slate-500 mx-2">–</span>
                            {formatPrice(result.ai_estimate_high)}
                        </div>
                        <div className="inline-flex items-center gap-1.5 text-xs text-electric mt-3 uppercase tracking-[0.18em] bg-electric/10 border border-electric/20 px-2.5 py-1 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-electric" />
                            Confidence: {result.ai_confidence || "high"}
                        </div>
                        <div className="text-xs text-slate-500 mt-2">
                            Indicative only · excludes GST
                        </div>
                    </div>

                    <div>
                        <div className="text-slate-400 text-xs uppercase tracking-[0.18em] mb-3">
                            What we noticed
                        </div>
                        <p
                            data-testid="ai-summary"
                            className="text-slate-200 leading-relaxed"
                        >
                            {result.ai_summary}
                        </p>

                        {result.ai_recommendations?.length > 0 && (
                            <ul
                                data-testid="ai-recommendations"
                                className="mt-5 space-y-2"
                            >
                                {result.ai_recommendations.map((r, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-sm text-slate-300"
                                    >
                                        <Sparkles
                                            size={15}
                                            className="text-electric mt-0.5 shrink-0"
                                        />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <p className="text-slate-400 text-sm">
                        We&rsquo;ve emailed a copy to{" "}
                        <span className="text-white font-medium">
                            {result.email}
                        </span>{" "}
                        and we&rsquo;ll match you with a trusted local repairer
                        who confirms your exact price — usually within 24 hours.
                    </p>
                    <button
                        onClick={onReset}
                        data-testid="quote-reset"
                        className="shrink-0 px-5 py-2.5 text-sm uppercase tracking-[0.15em] text-white bg-white/5 border border-white/15 hover:border-electric/60 hover:bg-white/10 transition-all rounded-xl"
                    >
                        New Quote
                    </button>
                </div>

                {/* Refundable deposit CTA — hidden while building reputation (toggle SHOW_DEPOSIT_CTA) */}
                {SHOW_DEPOSIT_CTA && (
                <div
                    data-testid="deposit-cta"
                    className="mt-6 bg-gradient-to-br from-carbon2 to-navy rounded-3xl p-6 md:p-7 border border-electric/15 shadow-glow-cyan flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                >
                    <div className="flex items-start gap-3.5">
                        <span className="w-12 h-12 rounded-2xl bg-electric/10 grid place-items-center shrink-0">
                            <ShieldCheck
                                size={24}
                                className="text-electric"
                                strokeWidth={2}
                            />
                        </span>
                        <div>
                            <div className="font-display font-semibold text-xl text-white leading-tight">
                                Lock in my repair slot
                            </div>
                            <p className="text-slate-400 text-sm mt-1 max-w-md">
                                Secure a spot in our diary with a NZ$50 hold —
                                it comes straight off your final bill.
                            </p>
                            <span className="inline-flex items-center gap-1.5 mt-2.5 bg-electric/10 text-electric text-xs font-bold px-3 py-1 rounded-full">
                                <ShieldCheck size={13} strokeWidth={2.4} /> 100%
                                refundable if plans change
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={lockSlot}
                        disabled={locking}
                        data-testid="lock-slot-button"
                        className="shrink-0 px-7 py-4 text-sm uppercase tracking-wide text-white font-bold bg-safety hover:bg-safety-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-1 active:scale-95 shadow-glow-orange inline-flex items-center justify-center gap-2 rounded-2xl"
                    >
                        {locking ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Redirecting…
                            </>
                        ) : (
                            <>Pay NZ$50 hold</>
                        )}
                    </button>
                </div>
                )}
            </div>
        </div>
    );
}
